import { useCallback, useEffect, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { CustomNotification } from 'common/components';
import { IconFolderAsset } from 'common/components/folder/FolderTree';

import { useAsync, useCallbackRef, useControllableState } from 'hooks';

import { getFolderList, getItemsOfFolder } from 'services/new-folder';
import {
  getFolderOwnByUser,
  getFolderShortDetail,
  getFolderDetailGridThumb,
} from 'services/folder';

import {
  buildHierarchyTreeFolder,
  getHierarchyExpandedKeys,
  getNodeTreeData,
  mappingFolderToTreeData,
  updateTreeData,
  findNodeTreeById,
} from './utils';

import { sleep } from 'utils/delay';
import { sortFolderToTop } from 'pages/folders/shared/utils';
import { forwardTo } from 'utils/common/route';
import { toUniqueList } from 'utils';

import emptyFolderIcon from 'assets/folder/empty-folder.png';
import folderIcon from 'assets/folder/folder.png';

export const filterParams = {
  folder: [
    {
      fieldName: 'type',
      filterType: 'Equal',
      value: 'folder', //
    },
  ],
  all: [],
};

const defaultFolderParam = {
  isAllowViewDetail: false,
};

const defaultFileParam = {
  isAllowViewDetail: false,
};

const folderGridQueryKeys = (params) => {
  return ['folder', 'grid', params];
};

export const useGetFolderGridQuery = ({ params, ...options }) => {
  return useQuery({
    queryKey: folderGridQueryKeys(params),
    queryFn: async () => {
      const res = await getFolderDetailGridThumb(params);
      if (res.isSuccess) {
        return res?.data;
      } else {
        CustomNotification.error(res?.message || 'Folder not found');
      }
    },
    enabled: Boolean(params?.folderId),
    ...options,
  });
};

export const useGetFolderTreeData = (params) => {
  const {
    defaultTreeProp = [],
    defaultSelectedItem = {},
    defaultExpandedKeys = [],
    pageIndex = 1,
    pageSize = 20,
    filterType = 'all',
    folderParam = defaultFolderParam,
    fileParam = defaultFileParam,
    navigatedIdFolder,
  } = params;

  const queryClient = useQueryClient();

  const { data: folderDetail } = useGetFolderDetail({
    folderId: navigatedIdFolder,
    isLoadContent: false,
  });

  const { hierarchy, level } = folderDetail || {};
  const isRoot = level === 1;

  const [statusGenerateTree, setStatusGenerateTree] = useState('idle');
  const [treeData, setTreeData] = useControllableState({
    defaultProp: defaultTreeProp,
  });

  const [expandedKeys, setExpandedKeys] = useControllableState({
    defaultProp: defaultExpandedKeys,
  });
  const [loadedKeys, setLoadedKeys] = useState([]);

  const [selectedItem, setSelectedItem] = useControllableState({
    defaultProp: defaultSelectedItem,
  });

  const handleUpdateTreeData = useCallback(
    (data) => setTreeData(data),
    [setTreeData]
  );

  const handleUpdateExpandedKeys = useCallback(
    (keys) => setExpandedKeys(keys),
    [setExpandedKeys]
  );

  const handleSelectNodeById = useCallbackRef((id) => {
    const foundNode = findNodeTreeById(treeData, id);

    if (!foundNode) return false; //* 4641: cannot found nested folder, so return result to rebuild tree

    setSelectedItem({
      selectedKeys: [foundNode.key],
      selectedFolder: foundNode.data,
    });
    setExpandedKeys(expandedKeys.concat([foundNode.key]));

    return foundNode;
  });

  const handleUpdateSelectedItem = useCallbackRef((item) => {
    const wasClickOnSameItem = selectedItem?.selectedKeys?.includes(
      item?.info?.node?.key
    );

    const isFolderType =
      item?.info?.node?.data?.type?.toLowerCase() === 'folder';

    if (wasClickOnSameItem) return;

    setSelectedItem({
      selectedKeys: item?.selectedKeys,
      selectedFolder: item?.info?.node?.data,
    });

    isFolderType &&
      setExpandedKeys(toUniqueList(expandedKeys.concat(item.selectedKeys)));
  });

  const handleUpdateLoadedKeys = useCallback(
    (keys) =>
      setLoadedKeys((prevVal) => {
        if (prevVal?.includes(keys)) {
          return prevVal;
        }
        return prevVal?.concat(keys);
      }),
    [setLoadedKeys]
  );

  const handleBuildRootTree = useCallbackRef(() => {
    const rootNode = getNodeTreeData({
      node: { ...folderDetail, type: 'folder' }, // folder doesn't have type value !!!
      folderParam,
      fileParam,
    });
    setExpandedKeys([rootNode.key]);
    setStatusGenerateTree('success');
    setSelectedItem({
      selectedKeys: [rootNode?.key],
      selectedFolder: rootNode?.data,
    });
    handleUpdateTreeData([rootNode]);
  });

  const handleBuildHierarchyTree = useCallbackRef(async (hierarchyId) => {
    const listIds = hierarchyId.split('/').filter(Boolean); //

    setStatusGenerateTree('loading');

    try {
      const hierarchyTreeData = await buildHierarchyTreeFolder({
        listIds,
        folderParam,
        fileParam,
        filterType,
      });
      const foundNode = findNodeTreeById(hierarchyTreeData, navigatedIdFolder);

      const expandedKeys = getHierarchyExpandedKeys(
        hierarchyTreeData,
        listIds.map((id) => parseInt(id))
      );

      setExpandedKeys(expandedKeys);
      setLoadedKeys(expandedKeys);
      setSelectedItem({
        selectedKeys: [foundNode?.key],
        selectedFolder: foundNode?.data,
      });
      setStatusGenerateTree('success');
      handleUpdateTreeData(hierarchyTreeData);

      return {
        foundNode,
        hierarchyTreeData,
      };
    } catch (error) {
      setStatusGenerateTree('error');
      handleUpdateTreeData([]);
    }
  });

  useEffect(() => {
    if (isRoot) {
      handleBuildRootTree();
      return;
    }

    //* build tree from subfolder
    if (hierarchy) {
      // hierarchy is parent path
      const fullHierarchyId = `${hierarchy}/${navigatedIdFolder}`;
      handleBuildHierarchyTree(fullHierarchyId);
    }
  }, [
    isRoot,
    hierarchy,
    handleBuildHierarchyTree,
    handleBuildRootTree,
    navigatedIdFolder,
  ]);

  // Need to catch error, this function is using pageSize, pageIndex as params. Wrap this function with useCallbackRef ???
  const handleLoadMoreFolder = async ({ key, children, data }) => {
    if (loadedKeys.includes(key)) return;

    const { id: folderId } = data;

    const params = {
      folderId,
      pageIndex, // change later
      pageSize, // change later
      filters: filterParams[filterType],
      sort: [
        {
          fieldName: 'id',
          isAscending: false,
        },
      ], // new first,
    };

    try {
      const response = await queryClient.fetchQuery({
        queryKey: folderGridQueryKeys({
          folderId,
          pageIndex,
          pageSize,
          filters: filterParams[filterType],
          sort: params.sort,
        }),
        queryFn: async () => {
          const res = await getFolderDetailGridThumb(params);
          if (res?.isSuccess) {
            return res;
          } else {
            throw new Error(res?.message || 'Failed to load folder items');
          }
        },
        staleTime: 0,
      });

      return new Promise(async (resolve) => {
        if (children || response === null) {
          resolve();
          return;
        }

        const sortedData = response?.data?.gridData?.sort(sortFolderToTop);

        const mappedTreeData = mappingFolderToTreeData(
          sortedData,
          folderParam,
          fileParam
        );

        await sleep(500); // sleep a little bit to update tree (get idea from antd :))
        setTreeData((prevTreeData) =>
          updateTreeData(prevTreeData, key, mappedTreeData)
        );
        resolve();
      });
    } catch (error) {
      CustomNotification.error(error?.message || 'Failed to load folder items');
      return Promise.resolve(); // Resolve to prevent hanging
    }
  };

  const handleSetStatusTree = (status) => {
    setStatusGenerateTree(status);
  };

  const handleSelectDeepNestedFolder = (folder) => {
    const folderId = folder?.type?.toLowerCase() === 'folder' && folder?.id;

    if (!folderId) return;

    const foundTreeNode = handleSelectNodeById(folderId);

    if (!foundTreeNode) rebuildTreeToSelectFolder(folder);
  };

  const removeNodeFromTree = useCallback((data, targetKey) => {
    return data.filter((item) => {
      if (item.key === targetKey) {
        return false;
      }
      if (item.children) {
        item.children = removeNodeFromTree(item.children, targetKey);
      }
      return true;
    });
  }, []);

  const addNodeToTree = useCallback((data, targetKey, nodeToAdd) => {
    return data.map((item) => {
      if (item.key === targetKey) {
        const newChildren = item.children
          ? [...item.children, nodeToAdd]
          : [nodeToAdd];
        return { ...item, children: newChildren };
      }

      if (item.children) {
        return {
          ...item,
          children: addNodeToTree(item.children, targetKey, nodeToAdd),
        };
      }

      return item;
    });
  }, []);

  const updateNodeWithFreshData = (node, targetKey, freshData) => {
    if (node.key === targetKey) {
      const isLeaf = freshData.subFolderCount === 0;
      return {
        ...node,
        data: {
          ...node.data,
          ...freshData, // Update with all fresh data from server
        },
        isLeaf: isLeaf,
        icon: <IconFolderAsset src={isLeaf ? emptyFolderIcon : folderIcon} />,
      };
    }

    if (node.children) {
      return {
        ...node,
        children: node.children.map((child) =>
          updateNodeWithFreshData(child, targetKey, freshData)
        ),
      };
    }

    return node;
  };

  const handleUpdateTreeAfterMoving = async (
    selectedFolder,
    destinationFolder
  ) => {
    const nodeToMove = findNodeTreeById(treeData, selectedFolder?.id);

    if (!nodeToMove) {
      return;
    }

    const oldHierarchiesOfSelected =
      selectedFolder?.hierarchy?.split('/').filter(Boolean) ?? [];

    const oldParentId =
      oldHierarchiesOfSelected[oldHierarchiesOfSelected?.length - 1];

    let oldParentNode = oldParentId
      ? findNodeTreeById(treeData, oldParentId)
      : null;

    // Remove the node from tree
    let updatedTreeAfterRemoval = removeNodeFromTree(treeData, nodeToMove.key);

    // Array to collect folder IDs that need to be refetched
    const foldersToRefetch = [];

    // Add old parent to refetch list
    if (oldParentId) {
      foldersToRefetch.push(oldParentId);
    }

    // Add destination to refetch list
    if (destinationFolder?.id) {
      foldersToRefetch.push(destinationFolder.id);
    }

    // Refetch all affected folders to get latest data
    const refetchPromises = foldersToRefetch.map(async (folderId) => {
      return refetchFolderDetail(folderId, queryClient);
    });

    try {
      // Wait for all refetches to complete
      const updatedFolders = await Promise.all(refetchPromises);

      // Update old parent node with fresh data
      if (oldParentNode && updatedFolders[0]) {
        const updatedOldParentData = updatedFolders[0];

        // Update the old parent node with fresh data
        updatedTreeAfterRemoval = updatedTreeAfterRemoval.map((item) =>
          updateNodeWithFreshData(item, oldParentNode.key, updatedOldParentData)
        );
      }

      // Handle destination folder
      if (destinationFolder && destinationFolder.id) {
        const destinationNode = findNodeTreeById(
          updatedTreeAfterRemoval,
          destinationFolder?.id
        );

        if (destinationNode) {
          // Get the updated destination data (it's the second item if old parent exists, first if not)
          const updatedDestinationData = oldParentId
            ? updatedFolders[1]
            : updatedFolders[0];

          if (updatedDestinationData) {
            // Update destination node with fresh data
            updatedTreeAfterRemoval = updatedTreeAfterRemoval.map((item) =>
              updateNodeWithFreshData(
                item,
                destinationNode.key,
                updatedDestinationData
              )
            );

            // If destination was previously empty but now has items, remove from expanded keys to force reload
            if (
              destinationNode.data.subFolderCount === 0 &&
              updatedDestinationData.subFolderCount > 0
            ) {
              setExpandedKeys((prevKeys) => {
                const newExpandedKeys = prevKeys.filter(
                  (key) => key !== destinationNode.key
                );
                return newExpandedKeys;
              });
            }
          }

          if (loadedKeys.includes(destinationNode.key)) {
            // Destination is loaded, add the node directly
            updatedTreeAfterRemoval = addNodeToTree(
              updatedTreeAfterRemoval,
              destinationNode.key,
              nodeToMove
            );

            setExpandedKeys((prevKeys) => {
              return prevKeys.concat(destinationNode.key);
            });
          } else {
            // Destination not loaded, invalidate it
            setLoadedKeys((prevKeys) => {
              const newLoadedKeys = prevKeys.filter(
                (key) => key !== destinationNode.key
              );
              return newLoadedKeys;
            });

            // If destination is expanded, reload it
            if (expandedKeys.includes(destinationNode.key)) {
              await handleLoadMoreFolder({
                key: destinationNode.key,
                children: null,
                data: updatedDestinationData || destinationNode.data,
              });
            }
          }
        }
      } else {
        // Moving to root
        updatedTreeAfterRemoval = [...updatedTreeAfterRemoval, nodeToMove];
      }

      setTreeData(updatedTreeAfterRemoval);
    } catch (error) {
    } finally {
      queryClient.invalidateQueries({
        queryKey: keys.getFolderDetail({
          folderId: selectedFolder?.id,
          isLoadContent: false,
        }),
      });
    }
  };

  // Alternative: More concise version
  const handleMoveOutsideTree = async (selectedFolder) => {
    try {
      // Find and remove the node
      const nodeToMove = findNodeTreeById(treeData, selectedFolder?.id);
      if (!nodeToMove) return;

      // Get parent ID
      const parentId = selectedFolder?.hierarchy
        ?.split('/')
        .filter(Boolean)
        .pop();

      // Remove from tree
      let updatedTree = removeNodeFromTree(treeData, nodeToMove.key);

      // Update parent if exists
      if (parentId) {
        const parentNode = findNodeTreeById(updatedTree, parentId);
        if (parentNode) {
          const freshData = await refetchFolderDetail(parentId, queryClient);
          if (freshData) {
            updatedTree = updateNodeWithFreshData(
              updatedTree,
              parentNode.key,
              freshData
            );
          }
        }
      }

      setTreeData(updatedTree);
      setSelectedItem({ selectedKeys: [], selectedFolder: null });
    } catch (error) {}
  };

  const rebuildTreeToSelectFolder = async (selectedFolder) => {
    if (!selectedFolder) return;

    const buildingHierarchy = `${selectedFolder.hierarchy}/${selectedFolder.id}`;

    const { foundNode } = await handleBuildHierarchyTree(buildingHierarchy);

    if (!foundNode) return;

    //* if foundNode mean building succeed
    await sleep(1); //* make sure state updated
    handleSelectNodeById(selectedFolder.id);
  };

  return {
    isLoadingTree:
      statusGenerateTree === 'idle' || statusGenerateTree === 'loading',
    treeData,
    expandedKeys,
    loadedKeys,
    selectedItem,
    handleSelectNodeById,
    handleUpdateSelectedItem,
    handleUpdateTreeData,
    handleLoadMoreFolder,
    handleUpdateExpandedKeys,
    handleUpdateLoadedKeys,
    handleSetStatusTree,
    handleBuildHierarchyTree,
    handleSelectDeepNestedFolder,
    handleUpdateTreeAfterMoving,
    handleMoveOutsideTree,
  };
};

export const useGetAllFolderGrid = ({ isEnabled, onResetParentId }) => {
  const { data: initialData, run, status, setData } = useAsync();

  const [fetchMoreFolderLoading, setFetchMoreFolderLoading] = useState(false);

  const handleCacheListFolder = (key, data) => {
    setData({
      ...initialData,
      [key]: data,
    });
  };

  const getGridFolder = () => {
    //? need to improve: use load on scroll for better performance
    const params = {
      folderId: null,
      pageIndex: 1,
      pageSize: 9999,
      filters: [
        {
          fieldName: 'isOwner',
          filterType: 'equal',
          value: true,
        },
      ],
      sort: [
        {
          fieldName: 'id',
          isAscending: false,
        },
      ],
    };

    run(getFolderList(params));
  };

  const handleSearchFolder = async (searchText) => {
    onResetParentId?.();
    handleCacheListFolder('home', []);

    if (!searchText) {
      getGridFolder();
      return;
    }

    try {
      const { data, isSuccess } = await getFolderOwnByUser({
        Search: searchText,
      });
      if (isSuccess) {
        handleCacheListFolder('home', data?.folderList);
      }
    } catch (error) {
      CustomNotification.error(error?.message ?? 'Folder is not found');
    }
  };

  const handleFetchMoreFolder = async (folderId) => {
    const params = {
      folderId,
      pageIndex: 1, // change later
      pageSize: 9999, // change later
      filters: [
        {
          fieldName: 'isOwner',
          filterType: 'equal',
          value: true,
        },
        {
          fieldName: 'type',
          filterType: 'equal',
          value: 'folder',
        },
      ],
      sort: [
        {
          fieldName: 'id',
          isAscending: false,
        },
      ],
    };
    try {
      setFetchMoreFolderLoading(true);
      const { isSuccess, data, message } =
        (await getItemsOfFolder(params)) ?? {};
      if (isSuccess) {
        handleCacheListFolder(folderId, data?.gridData);
      } else {
        CustomNotification.error(message ?? 'Folder is not found');
      }
    } catch (error) {
      CustomNotification.error(error?.message ?? 'Folder is not found');
    }

    setFetchMoreFolderLoading(false);
  };
  useEffect(() => {
    if (isEnabled) {
      getGridFolder();
    }
  }, [run, isEnabled]);

  useEffect(() => {
    if (!initialData?.['home'] && initialData?.gridData)
      setData({
        home: initialData?.gridData ?? [],
      });
  }, [JSON.stringify(initialData)]);

  return {
    isLoading: status === 'pending' || fetchMoreFolderLoading,
    folderData: initialData,
    getGridFolder,
    handleSearchFolder,
    handleFetchMoreFolder,
  };
};

export const keys = {
  getFolderDetail: ({ folderId, isLoadContent }) => [
    'folder',
    'short-content-pane',
    {
      params: {
        id: parseInt(folderId),
        isLoadContent: isLoadContent,
      },
    },
  ],
};

export const fetchFolderDetail = async ({ id, isLoadContent = false }) => {
  const res = await getFolderShortDetail({
    id,
    isLoadContent,
  });

  if (res.isSuccess) {
    return res.data;
  }
  CustomNotification.error(res?.message || 'Folder not found');
  forwardTo('/folders');
};

export const useGetFolderDetail = ({
  folderId,
  isLoadContent = false,
  ...options
}) => {
  return useQuery({
    queryKey: keys.getFolderDetail({ folderId, isLoadContent }),
    queryFn: async () => {
      const res = await getFolderShortDetail({
        id: folderId,
        isLoadContent,
      });
      if (res.isSuccess) {
        return res?.data;
      } else {
        CustomNotification.error(res?.message || 'Folder not found');
        forwardTo('/folders');
      }
    },

    enabled: Boolean(folderId),
    ...options,
  });
};

export const refetchFolderDetail = async (folderId, queryClient) => {
  if (!folderId) return null;

  const queryKey = keys.getFolderDetail({ folderId, isLoadContent: false });

  // Invalidate first to ensure fresh data
  await queryClient.invalidateQueries({ queryKey });

  return await queryClient.fetchQuery({
    queryKey,
    queryFn: () => fetchFolderDetail({ id: folderId, isLoadContent: false }),
  });
};
